/**
 * Hook for fetching and managing Wealthica v2 account data
 * 
 * Provides account data, status, and persistence management for connected Wealthica accounts.
 * Uses proper utility modules and maintains navigation-aware caching.
 */

// React hooks
import { useCallback, useEffect, useRef, useState } from 'react';

// External libraries
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Internal context and services  
import { useAuthStore } from '@/stores/authStore';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { logger } from '@/services/LoggingService';

// Wealthica utilities and cache management 
import { 
  getSessionInitialized, 
  setSessionInitialized,
  isDailyRefreshNeeded,
  setLastRefresh,
  clearCacheOnAuthFailure
} from '../utils/cache-management';

import { handleAuthError, ensureValidToken } from '@/services/AuthAwareApiService';
import { useConnectionEvents } from '../utils/connection-utils';

import { useAccountsFromStore } from '../utils/account-utils';
// Removed unused createAccountsQueryFn import after cutover
import { executeSaveOperation, executeDeleteOperation } from './accounts-save-operations';
import { PerformanceMonitor } from '@/utils/performance-monitor';
import { ACCOUNT_QUERY_CONFIG, createQueryKey } from '../utils/query-config';
// Cutover: frontend no longer reads tokens directly; DB→UI hook removed from UI layer

// Types
import type { WealthicaAccount } from '../types';

// Processing state enum
enum ConnectionProcessingState {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  SYNCING = 'syncing',
  ERROR = 'error'
}

interface UseWealthica2AccountsParams {
  userId?: string;
  token?: string;
  persist?: boolean;
  onSave?: () => void;
  onDelete?: () => void;
  onRefresh?: () => void;
}

/**
 * Hook for fetching and managing Wealthica v2 account data
 * 
 * @param params - Parameters for the hook
 * @param params.userId - User ID
 * @param params.token - Bearer token
 * @param params.persist - Whether to persist the account data for future visits
 
 */
export function useWealthica2Accounts(params: UseWealthica2AccountsParams = {}) {
  const { persist = false, onSave, onDelete } = params;
  
  // Store mode selection
  
  // === PHASE 2: SMART ROUTING LOGIC ===
  // Detect if this is a read-only consumer that should use store
  const isReadOnlyConsumer = persist === false && 
                            !onSave && 
                            !onDelete &&
                            !params.token &&  // Not providing custom token
                            !params.userId;   // Not providing custom userId
  
  // ALWAYS call hooks unconditionally (Rules of Hooks compliance)
  const storeResult = useAccountsFromStore();
  // Do not early-return here; always call hooks unconditionally. We'll decide which result to return at the end.
  
  // === DATA OWNER PATH: Full React Query + API Logic ===

  // === PHASE 3: PERFORMANCE VERIFICATION ===
  // Log that this call uses full API path
  PerformanceMonitor.logApiCall({
    persist,
    hasOnSave: !!onSave,
    hasOnDelete: !!onDelete,
    path: 'useWealthica2Accounts_FULL_PATH'
  });
  
  // Get auth and database connections
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  
  // Connection state - multi-store only
  const multiStore = useMultiWealthica2Store();
  
  const wealthicaConnectionData = (multiStore.activeConnectionId ? 
    multiStore.getConnection(multiStore.activeConnectionId)?.connectionData : 
    multiStore.getAllConnections()[0]?.connectionData) || null;
  
  // Multi-store: subscribe to connection count for enabling/query key
  const connectionCount = useMultiWealthica2Store((s) => s.connections.size);
  
  // Connection status check
  
  // Get data from parameters; frontend does not read tokens from storage in cutover
  const bearerToken = params.token || null;
  const currentUserId = params.userId || user?.id;
  
  // Processing state
  const [processingState, setProcessingState] = useState<ConnectionProcessingState>(ConnectionProcessingState.IDLE);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const isProcessing = processingState !== ConnectionProcessingState.IDLE || isRefreshingToken;
  
  // Bridge helper: helpers accept string states; normalize to our enum setter
  const setProcessingStateString = useCallback((state: string) => {
    switch (state) {
      case 'idle':
        setProcessingState(ConnectionProcessingState.IDLE);
        break;
      case 'syncing':
        setProcessingState(ConnectionProcessingState.SYNCING);
        break;
      case 'error':
        setProcessingState(ConnectionProcessingState.ERROR);
        break;
      default:
        setProcessingState(ConnectionProcessingState.IDLE);
    }
  }, []);
  
  // Refs for persistence tracking
  const prevPersist = useRef(persist);
  const isInitializedRef = useRef(false);
  const persistenceExecutionRef = useRef<Set<string>>(new Set());
  
  // Connection data state
  const isWealthicaConnected = !!wealthicaConnectionData;
  
  // Navigation awareness setup with daily refresh check
  useEffect(() => {
    const isNavigation = getSessionInitialized();
    if (isNavigation) {
      isInitializedRef.current = true;
      
      // Check if daily refresh is needed (only on user login/access)
      if (isWealthicaConnected && isDailyRefreshNeeded()) {
        // Trigger a quiet refresh without showing loading states
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: createQueryKey('wealthica2', 'accounts', {
              bearerToken,
              userId: currentUserId,
              institutionId: wealthicaConnectionData?.institutionId
            })
          });
          setLastRefresh();
        }, 1000);
      }
    } else {
      setSessionInitialized();
    }
  }, [isWealthicaConnected, queryClient, bearerToken, currentUserId, wealthicaConnectionData?.institutionId]);
  
  // React Query for accounts - Only enabled for data owners, NOT read-only consumers
  const queryResult = useQuery<WealthicaAccount[]>({
    queryKey: createQueryKey('wealthica2', 'accounts', {
      // Token excluded from key in server-only token model
      userId: currentUserId,
      institutionId: wealthicaConnectionData?.institutionId,
      connectionCount
    }),
    queryFn: async (): Promise<WealthicaAccount[]> => {
      // Multi-store mode: use multi-store actions
      await multiStore.fetchAccountsForAllConnections(isInitializedRef.current);
      return multiStore.getAllAccounts();
    },
    enabled: !!(isWealthicaConnected && 
                !isReadOnlyConsumer && 
                connectionCount > 0 &&
                currentUserId), // Prevent React Query for read-only consumers; server-only token model
    ...ACCOUNT_QUERY_CONFIG
  });

  // Connection events handling
  useConnectionEvents(wealthicaConnectionData, queryResult.isLoading);
  // Loading state
  const isWealthicaLoading = queryResult.isLoading || queryResult.isFetching;
  
  // No singleton store syncing in cutover; multi-store already holds canonical state

  // Save function
  const triggerSave = useCallback(async () => {
    if (!currentUserId || !wealthicaConnectionData || !queryResult.data) {
      logger.error('useWealthica2Accounts', 'Cannot save - missing required data');
      return;
    }

    await executeSaveOperation(
      currentUserId,
      wealthicaConnectionData,
      queryResult.data,
      persistenceExecutionRef,
      setProcessingStateString
    );
  }, [currentUserId, wealthicaConnectionData, queryResult.data, bearerToken, setProcessingStateString]);

  // Delete function
  const triggerDelete = useCallback(async () => {
    if (!currentUserId || !wealthicaConnectionData) {
      logger.error('useWealthica2Accounts', 'Cannot delete - missing required data');
      return;
    }

    await executeDeleteOperation(
      currentUserId,
      wealthicaConnectionData,
      persistenceExecutionRef,
      setProcessingStateString,
      queryClient
    );
  }, [currentUserId, wealthicaConnectionData, queryClient, setProcessingStateString]);

  // Persistence toggle effect
  useEffect(() => {
    const isToggleOn = persist && !prevPersist.current;
    
    if (!isToggleOn) return;
    
    if (!currentUserId) {
      logger.error('useWealthica2Accounts', 'Cannot save - user not authenticated');
      return;
    }
    
    if (!wealthicaConnectionData) {
      logger.error('useWealthica2Accounts', 'Cannot save - no connection data');
      return;
    }
    
    if (!queryResult.data || (queryResult.data?.length || 0) === 0) {
      logger.error('useWealthica2Accounts', 'Cannot save - no accounts data');
      return;
    }
    
    if (isProcessing) {
      logger.error('useWealthica2Accounts', 'Cannot save - processing in progress');
      return;
    }
    
    // Persistence toggle turned ON
    
    triggerSave();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerSave function reference changes on every render, causing infinite loops
  }, [persist, queryResult.data, currentUserId, wealthicaConnectionData, isProcessing]);

  // Persistence cleanup effect
  useEffect(() => {
    const isToggleOff = !persist && prevPersist.current;
    
    // Only take action when there's an actual toggle off event
    if (isToggleOff && currentUserId && wealthicaConnectionData) {
      triggerDelete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerDelete function reference changes on every render, causing infinite loops
  }, [persist, currentUserId, wealthicaConnectionData]);

  // Update previous persist state after both effects have run
  useEffect(() => {
    prevPersist.current = persist;
  }, [persist]);

  // Ensure accounts is always an array
  const ensuredAccounts = Array.isArray(queryResult.data) ? queryResult.data : [];
  
  // Get last sync timestamp and check data availability
  const lastSyncAt = wealthicaConnectionData?.lastSyncAt || null;
  const hasTokenExpired = false; // Server-only token model
  const hasPersistedData = !!lastSyncAt && (ensuredAccounts?.length || 0) > 0 && !!persist;
  
  // Route read-only consumers to Zustand store data, otherwise return full query path result
  if (isReadOnlyConsumer) {
    return storeResult;
  }

  return {
    wealthicaAccounts: ensuredAccounts,
    isWealthicaConnected,
    isWealthicaLoading,
    wealthicaError: queryResult.error,
    isWealthicaRefetching: queryResult.isRefetching,
    institutionId: wealthicaConnectionData?.institutionId,
    providerName: wealthicaConnectionData?.providerName,
    connectionId: wealthicaConnectionData?.connectionId,
    lastSyncAt,
    hasTokenExpired,
    hasPersistedData
  };
}
